import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check, FileText, Folder, FolderLock, Lock, Pencil, Plus, Search,
  ShieldCheck, Trash2, Upload, Users, X,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context';
import {
  apiCreatePrivateFolder,
  apiDeletePrivateFile,
  apiDeletePrivateFolder,
  apiGetClients,
  apiGetPrivateFolder,
  apiGetPrivateFolders,
  apiGetTeam,
  apiUpdatePrivateFolder,
  apiUpdatePrivateFolderAccess,
  apiUpdatePrivateFolderNotes,
} from '../../api';
import { formatBytes, uploadPrivateFile } from '../../uploads';
import FileLink from '../FileLink';
import ModalShell from '../Modal/ModalShell';

const idOf = (value) => String(value?._id || value?.id || value || '');

function FolderFormModal({ folder, clients, team, currentUserId, saving, onClose, onSave }) {
  const [name, setName] = useState(folder?.name || '');
  const [clientId, setClientId] = useState(idOf(folder?.client));
  const [collaboratorIds, setCollaboratorIds] = useState(
    (folder?.collaborators || []).map(idOf)
  );
  const creating = !folder;

  const selectClient = (event) => {
    const nextId = event.target.value;
    const previousClient = clients.find((client) => idOf(client) === clientId);
    const nextClient = clients.find((client) => idOf(client) === nextId);
    if (!name.trim() || name.trim() === previousClient?.name) setName(nextClient?.name || '');
    setClientId(nextId);
  };

  const toggleCollaborator = (memberId) => {
    setCollaboratorIds((current) => current.includes(memberId)
      ? current.filter((id) => id !== memberId)
      : [...current, memberId]);
  };

  const submit = (event) => {
    event.preventDefault();
    onSave({ name: name.trim(), clientId: clientId || null, collaboratorIds });
  };

  const availableTeam = team.filter((member) => idOf(member) !== currentUserId);

  return (
    <ModalShell
      title={creating ? 'Create private folder' : 'Folder settings'}
      subtitle={creating ? 'Link it to a client or use your own folder name.' : 'Rename this folder or change its client reference.'}
      eyebrow="Private Space"
      icon={FolderLock}
      size="md"
      onClose={onClose}
    >
      <form className="popup-form private-folder-form" onSubmit={submit}>
        <label className="popup-field">
          <span className="field-label">Reference a client (optional)</span>
          <select className="input" value={clientId} onChange={selectClient}>
            <option value="">No client — general private folder</option>
            {clients.map((client) => <option key={idOf(client)} value={idOf(client)}>{client.name} · {client.email}</option>)}
          </select>
          <small className="popup-field-help">The client is only a reference. They will never see this folder.</small>
        </label>

        <label className="popup-field">
          <span className="field-label">Folder name</span>
          <input className="input" value={name} onChange={(event) => setName(event.target.value)} maxLength={120} placeholder="e.g. Evercrest working files" autoFocus />
        </label>

        {creating && (
          <div className="private-folder-sharing-field">
            <div className="private-folder-sharing-heading">
              <span className="field-label">Collaborators (optional)</span>
              <small>Owner-only by default</small>
            </div>
            {availableTeam.length === 0 ? (
              <p className="private-folder-no-team">No other internal team members are available.</p>
            ) : (
              <div className="private-team-picker">
                {availableTeam.map((member) => {
                  const memberId = idOf(member);
                  const checked = collaboratorIds.includes(memberId);
                  return (
                    <button key={memberId} type="button" className={checked ? 'selected' : ''} onClick={() => toggleCollaborator(memberId)}>
                      <span className="private-team-avatar">{member.name?.charAt(0)?.toUpperCase()}</span>
                      <span><strong>{member.name}</strong><small>{member.role?.replace('_', ' ')}</small></span>
                      <span className="private-team-check">{checked && <Check size={13} />}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="private-folder-privacy-note"><Lock size={14} /><span>Private to you{collaboratorIds.length ? ` and ${collaboratorIds.length} selected teammate${collaboratorIds.length === 1 ? '' : 's'}` : ''}. Client accounts have no access.</span></div>

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={!name.trim() || saving}>{saving ? <span className="spinner" /> : creating ? 'Create folder' : 'Save changes'}</button>
        </div>
      </form>
    </ModalShell>
  );
}

function AccessModal({ folder, team, currentUserId, saving, onClose, onSave }) {
  const [selected, setSelected] = useState((folder.collaborators || []).map(idOf));
  const availableTeam = team.filter((member) => idOf(member) !== currentUserId);
  const toggle = (memberId) => setSelected((current) => current.includes(memberId)
    ? current.filter((id) => id !== memberId)
    : [...current, memberId]);

  return (
    <ModalShell title="Manage access" subtitle="Choose internal teammates who can work inside this folder." eyebrow={folder.name} icon={Users} size="sm" onClose={onClose}>
      <div className="private-access-owner"><ShieldCheck size={16} /><span><strong>You are the owner</strong><small>Only you can change access or delete this folder.</small></span></div>
      <div className="private-team-picker private-access-picker">
        {availableTeam.map((member) => {
          const memberId = idOf(member);
          const checked = selected.includes(memberId);
          return (
            <button key={memberId} type="button" className={checked ? 'selected' : ''} onClick={() => toggle(memberId)}>
              <span className="private-team-avatar">{member.name?.charAt(0)?.toUpperCase()}</span>
              <span><strong>{member.name}</strong><small>{member.email}</small></span>
              <span className="private-team-check">{checked && <Check size={13} />}</span>
            </button>
          );
        })}
      </div>
      <div className="private-folder-privacy-note"><Lock size={14} /><span>Clients can never access Private Space, even when a folder references them.</span></div>
      <div className="modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={() => onSave(selected)}>{saving ? <span className="spinner" /> : 'Update access'}</button>
      </div>
    </ModalShell>
  );
}

export default function PrivateSpacePage() {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const { jwtToken, user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [notes, setNotes] = useState('');
  const [notesStatus, setNotesStatus] = useState('saved');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [folderLoading, setFolderLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);
  const savedNotesRef = useRef('');
  const notesFolderRef = useRef('');
  const notesSequenceRef = useRef(0);
  const currentUserId = idOf(user);

  const loadFolderList = useCallback(async () => {
    const data = await apiGetPrivateFolders(jwtToken);
    setFolders(data || []);
    return data || [];
  }, [jwtToken]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([loadFolderList(), apiGetClients(jwtToken), apiGetTeam(jwtToken)])
      .then(([, clientData, teamData]) => {
        if (!active) return;
        setClients(clientData || []);
        setTeam(teamData || []);
        setError('');
      })
      .catch((err) => { if (active) setError(err.message || 'Private Space could not be loaded.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [jwtToken, loadFolderList]);

  const setCurrentFolder = useCallback((folder) => {
    setSelectedFolder(folder);
    const nextNotes = folder?.notes || '';
    setNotes(nextNotes);
    savedNotesRef.current = nextNotes;
    notesFolderRef.current = idOf(folder);
    setNotesStatus('saved');
  }, []);

  useEffect(() => {
    let active = true;
    if (!folderId) {
      setCurrentFolder(null);
      return () => { active = false; };
    }
    setFolderLoading(true);
    apiGetPrivateFolder(jwtToken, folderId)
      .then((folder) => { if (active) setCurrentFolder(folder); })
      .catch((err) => {
        if (!active) return;
        setError(err.message || 'This private folder could not be opened.');
        navigate('/portal/private-space', { replace: true });
      })
      .finally(() => { if (active) setFolderLoading(false); });
    return () => { active = false; };
  }, [folderId, jwtToken, navigate, setCurrentFolder]);

  useEffect(() => {
    const activeId = idOf(selectedFolder);
    if (!activeId || notesFolderRef.current !== activeId || notes === savedNotesRef.current) return undefined;
    const sequence = ++notesSequenceRef.current;
    setNotesStatus('saving');
    const timer = window.setTimeout(async () => {
      try {
        await apiUpdatePrivateFolderNotes(jwtToken, activeId, notes);
        if (sequence === notesSequenceRef.current && notesFolderRef.current === activeId) {
          savedNotesRef.current = notes;
          setNotesStatus('saved');
          setFolders((current) => current.map((folder) => idOf(folder) === activeId ? { ...folder, notes } : folder));
        }
      } catch (err) {
        if (sequence === notesSequenceRef.current) {
          setNotesStatus('error');
          setError(err.message || 'Folder notes could not be saved.');
        }
      }
    }, 800);
    return () => window.clearTimeout(timer);
  }, [jwtToken, notes, selectedFolder]);

  const visibleFolders = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return folders;
    return folders.filter((folder) => `${folder.name} ${folder.client?.name || ''}`.toLowerCase().includes(normalized));
  }, [folders, query]);

  const isOwner = selectedFolder && idOf(selectedFolder.owner) === currentUserId;

  const createFolder = async (payload) => {
    setSaving(true);
    setError('');
    try {
      const folder = await apiCreatePrivateFolder(jwtToken, payload);
      setFolders((current) => [folder, ...current]);
      setShowCreate(false);
      navigate(`/portal/private-space/${idOf(folder)}`);
    } catch (err) { setError(err.message || 'Folder could not be created.'); }
    finally { setSaving(false); }
  };

  const updateFolder = async (payload) => {
    setSaving(true);
    setError('');
    try {
      const folder = await apiUpdatePrivateFolder(jwtToken, idOf(selectedFolder), payload);
      setFolders((current) => current.map((item) => idOf(item) === idOf(folder) ? folder : item));
      setSelectedFolder({ ...folder, notes });
      setShowEdit(false);
    } catch (err) { setError(err.message || 'Folder could not be updated.'); }
    finally { setSaving(false); }
  };

  const updateAccess = async (collaboratorIds) => {
    setSaving(true);
    setError('');
    try {
      const folder = await apiUpdatePrivateFolderAccess(jwtToken, idOf(selectedFolder), collaboratorIds);
      setFolders((current) => current.map((item) => idOf(item) === idOf(folder) ? folder : item));
      setSelectedFolder({ ...folder, notes });
      setShowAccess(false);
    } catch (err) { setError(err.message || 'Folder access could not be updated.'); }
    finally { setSaving(false); }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !selectedFolder || uploading) return;
    setError('');
    try {
      const uploadedFiles = [];
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        setUploading({ name: file.name, progress: 0, current: index + 1, total: files.length });
        const uploaded = await uploadPrivateFile(jwtToken, idOf(selectedFolder), file, {
          onProgress: (progress) => setUploading({ name: file.name, progress, current: index + 1, total: files.length }),
        });
        uploadedFiles.push(uploaded);
      }
      setSelectedFolder((current) => ({ ...current, files: [...(current.files || []), ...uploadedFiles] }));
      await loadFolderList();
    } catch (err) { setError(err.message || 'Private file upload failed.'); }
    finally {
      setUploading(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeFile = async (file) => {
    if (!window.confirm(`Delete "${file.name}" from this private folder?`)) return;
    try {
      await apiDeletePrivateFile(jwtToken, idOf(selectedFolder), idOf(file));
      setSelectedFolder((current) => ({ ...current, files: current.files.filter((item) => idOf(item) !== idOf(file)) }));
      setFolders((current) => current.map((folder) => idOf(folder) === idOf(selectedFolder)
        ? { ...folder, files: (folder.files || []).filter((item) => idOf(item) !== idOf(file)) }
        : folder));
    } catch (err) { setError(err.message || 'File could not be deleted.'); }
  };

  const removeFolder = async () => {
    if (!window.confirm(`Delete the private folder "${selectedFolder.name}" and all files inside it? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await apiDeletePrivateFolder(jwtToken, idOf(selectedFolder));
      setFolders((current) => current.filter((folder) => idOf(folder) !== idOf(selectedFolder)));
      navigate('/portal/private-space');
    } catch (err) { setError(err.message || 'Folder could not be deleted.'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="private-space-loading"><span className="spinner" /><span>Opening Private Space…</span></div>;

  return (
    <div className="private-space-page">
      <header className="private-space-page-header">
        <div>
          <span className="workspace-eyebrow"><Lock size={11} /> Internal only</span>
          <h1 className="page-title">Private Space</h1>
          <p className="page-subtitle">Organize client working files, private notes, and team collaboration.</p>
        </div>
        <div className="private-space-security"><ShieldCheck size={16} /><span><strong>Invisible to clients</strong><small>Only invited internal teammates can enter a folder.</small></span></div>
      </header>

      {error && <div className="private-space-error" role="alert"><span>{error}</span><button type="button" onClick={() => setError('')}><X size={15} /></button></div>}

      <div className="private-space-layout">
        <aside className="private-folder-sidebar">
          <button type="button" className="btn btn-primary private-new-folder" onClick={() => setShowCreate(true)}><Plus size={16} /> New folder</button>
          <label className="private-folder-search"><Search size={14} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a folder" /></label>
          <div className="private-folder-list scroll-hidden">
            {visibleFolders.length === 0 ? (
              <div className="private-folder-list-empty"><Folder size={22} /><span>{query ? 'No matching folders' : 'No private folders yet'}</span></div>
            ) : visibleFolders.map((folder) => {
              const active = idOf(folder) === folderId;
              const owned = idOf(folder.owner) === currentUserId;
              return (
                <button key={idOf(folder)} type="button" className={`private-folder-list-item${active ? ' active' : ''}`} onClick={() => navigate(`/portal/private-space/${idOf(folder)}`)}>
                  <span className="private-folder-list-icon"><FolderLock size={17} /></span>
                  <span className="private-folder-list-copy"><strong>{folder.name}</strong><small>{folder.client?.name || 'General workspace'}</small></span>
                  <span className="private-folder-list-meta">{owned ? <Lock size={11} /> : <Users size={12} />}{(folder.files || []).length}</span>
                </button>
              );
            })}
          </div>
        </aside>

        <main className="private-folder-workspace">
          {folderLoading ? (
            <div className="private-space-empty"><span className="spinner" /></div>
          ) : !selectedFolder ? (
            <div className="private-space-empty">
              <span className="private-space-empty-icon"><FolderLock size={30} /></span>
              <h2>Your private client workspace</h2>
              <p>Create a folder or select one from the left. Keep files and working notes here without exposing anything to clients.</p>
              <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={16} /> Create your first folder</button>
            </div>
          ) : (
            <>
              <header className="private-folder-header">
                <div className="private-folder-title">
                  <span className="private-folder-title-icon"><FolderLock size={22} /></span>
                  <div>
                    <span className="workspace-eyebrow">{selectedFolder.client ? `Client reference · ${selectedFolder.client.name}` : 'General private folder'}</span>
                    <h2>{selectedFolder.name}</h2>
                    <p>Owned by {selectedFolder.owner?.name || 'you'}{selectedFolder.collaborators?.length ? ` · Shared with ${selectedFolder.collaborators.length} teammate${selectedFolder.collaborators.length === 1 ? '' : 's'}` : ' · Owner only'}</p>
                  </div>
                </div>
                {isOwner && (
                  <div className="private-folder-actions">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowAccess(true)}><Users size={14} /> Access</button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowEdit(true)}><Pencil size={14} /> Edit</button>
                    <button type="button" className="private-folder-delete" onClick={removeFolder} disabled={saving} title="Delete private folder"><Trash2 size={15} /></button>
                  </div>
                )}
              </header>

              <div className="private-folder-content">
                <section className="private-folder-notes-card">
                  <div className="private-folder-section-heading">
                    <span><FileText size={16} /><strong>Working notes</strong></span>
                    <span className={`personal-notes-status ${notesStatus}`}>{notesStatus === 'saving' ? 'Saving…' : notesStatus === 'error' ? 'Not saved' : 'Saved'}</span>
                  </div>
                  <textarea value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={100000} placeholder="Write private notes, plans, links, instructions, or anything else for this folder…" />
                </section>

                <section className="private-folder-files-card">
                  <div className="private-folder-section-heading">
                    <span><Folder size={16} /><strong>Files</strong><small>{selectedFolder.files?.length || 0}</small></span>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => fileInputRef.current?.click()} disabled={!!uploading}><Upload size={14} /> Upload files</button>
                    <input ref={fileInputRef} type="file" multiple hidden onChange={(event) => uploadFiles(event.target.files)} />
                  </div>

                  <div
                    className={`private-file-dropzone${uploading ? ' uploading' : ''}`}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => { event.preventDefault(); uploadFiles(event.dataTransfer.files); }}
                  >
                    {uploading ? (
                      <div className="private-upload-progress">
                        <span><Upload size={17} /> Uploading {uploading.current} of {uploading.total}</span>
                        <strong>{uploading.name}</strong>
                        <div><span style={{ width: `${uploading.progress}%` }} /></div>
                        <small>{uploading.progress}%</small>
                      </div>
                    ) : selectedFolder.files?.length ? (
                      <div className="private-file-grid">
                        {selectedFolder.files.map((file) => (
                          <article key={idOf(file)} className="private-file-card">
                            <FileLink file={file} className="private-file-link" iconSize={17} />
                            <div className="private-file-details">
                              <span>{formatBytes(file.size)}</span>
                              <span>{file.uploadedBy?.name || 'Team member'}</span>
                              <time>{file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</time>
                            </div>
                            <button type="button" className="private-file-delete" onClick={() => removeFile(file)} title="Delete file"><Trash2 size={14} /></button>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <button type="button" className="private-file-empty" onClick={() => fileInputRef.current?.click()}>
                        <span><Upload size={22} /></span><strong>Drop files here</strong><small>or click to browse · up to 500 MB each</small>
                      </button>
                    )}
                  </div>
                </section>
              </div>
            </>
          )}
        </main>
      </div>

      {showCreate && <FolderFormModal clients={clients} team={team} currentUserId={currentUserId} saving={saving} onClose={() => setShowCreate(false)} onSave={createFolder} />}
      {showEdit && selectedFolder && <FolderFormModal folder={selectedFolder} clients={clients} team={team} currentUserId={currentUserId} saving={saving} onClose={() => setShowEdit(false)} onSave={updateFolder} />}
      {showAccess && selectedFolder && <AccessModal folder={selectedFolder} team={team} currentUserId={currentUserId} saving={saving} onClose={() => setShowAccess(false)} onSave={updateAccess} />}
    </div>
  );
}
